import React from 'react';
import { 
  SkeletonHero, 
  SkeletonCard,
  SkeletonTitle,
  SkeletonText,
  SkeletonParagraph,
  SkeletonButton,
  SkeletonStats,
  Skeleton
} from '../ui/skeletons';

// Home Page Full Skeleton
const HomePageSkeleton = () => (
  <div className="bg-[#FAF6F1] min-h-screen">
    {/* Header Skeleton */}
    <div className="fixed top-0 left-0 right-0 z-50 px-3 py-3">
      <div className="bg-white/80 backdrop-blur-md rounded-2xl px-6 py-4 flex items-center justify-between">
        <Skeleton className="w-32 h-8 rounded-lg" />
        <div className="hidden lg:flex items-center gap-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="w-20 h-4 rounded" />
          ))}
        </div>
        <SkeletonButton size="md" />
      </div>
    </div>

    <main>
      {/* Hero Section Skeleton */}
      <section className="min-h-screen relative overflow-hidden p-3">
        <div className="window-content bg-gradient-to-br from-[#D4E0D0] via-[#E0EAD8] to-[#D4E0D0] rounded-3xl min-h-[calc(100vh-1.5rem)] relative flex items-center">
          <div className="max-w-[1400px] mx-auto px-8 lg:px-16 py-24 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left - Text */}
              <div className="space-y-6">
                <SkeletonText width="100px" className="h-3" />
                <SkeletonTitle size="2xl" className="w-full" />
                <SkeletonTitle size="xl" className="w-3/4" />
                <SkeletonParagraph lines={3} className="max-w-md" />
                <div className="flex flex-wrap gap-4 pt-4">
                  <SkeletonButton size="xl" className="w-44" />
                  <SkeletonButton size="xl" className="w-36" />
                </div>
              </div>
              
              {/* Right - Notebook Image */}
              <div className="relative">
                <Skeleton className="w-full h-[400px] lg:h-[500px] rounded-3xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section Skeleton */}
      <section className="py-4 px-3">
        <div className="window-content bg-white rounded-3xl py-16 px-8">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonStats key={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section Skeleton */}
      <section className="py-4 px-3">
        <div className="window-content bg-gradient-to-br from-[#F5E6D3] via-[#FAF0E6] to-[#F5E6D3] rounded-3xl py-16 px-8">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <Skeleton className="w-full h-[400px] rounded-3xl" />
              
              {/* Text */}
              <div className="space-y-6">
                <SkeletonText width="80px" className="h-3" />
                <SkeletonTitle size="xl" className="w-3/4" />
                <SkeletonParagraph lines={4} />
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="w-8 h-8 rounded-full" />
                      <SkeletonText width="80px" className="h-4" />
                    </div>
                  ))}
                </div>
                <SkeletonButton size="lg" className="w-40" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section Skeleton */}
      <section className="py-4 px-3">
        <div className="window-content bg-gradient-to-br from-[#D4E0D0] via-[#E0EAD8] to-[#D4E0D0] rounded-3xl py-16 px-8">
          <div className="max-w-[1400px] mx-auto">
            {/* Title */}
            <div className="text-center mb-12">
              <SkeletonText width="100px" className="h-3 mx-auto mb-3" />
              <SkeletonTitle size="xl" className="mx-auto w-80" />
            </div>
            
            {/* Course Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="bg-white rounded-3xl overflow-hidden">
                  <Skeleton className="w-full h-48" />
                  <div className="p-8 space-y-4">
                    <SkeletonTitle size="lg" className="w-48" />
                    <SkeletonParagraph lines={3} />
                    <div className="flex items-center justify-between pt-4">
                      <div className="space-y-1">
                        <SkeletonText width="60px" className="h-3" />
                        <SkeletonText width="100px" className="h-6" />
                      </div>
                      <SkeletonButton size="lg" className="w-32" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Section Skeleton */}
      <section className="py-4 px-3">
        <div className="window-content bg-white rounded-3xl py-16 px-8">
          <div className="max-w-[1400px] mx-auto">
            {/* Title */}
            <div className="text-center mb-12">
              <SkeletonText width="120px" className="h-3 mx-auto mb-3" />
              <SkeletonTitle size="xl" className="mx-auto w-72" />
            </div>
            
            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="text-center space-y-4">
                  <Skeleton className="w-20 h-20 rounded-full mx-auto" />
                  <SkeletonTitle size="md" className="w-32 mx-auto" />
                  <SkeletonParagraph lines={3} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section Skeleton */}
      <section className="py-4 px-3">
        <div className="window-content bg-gradient-to-br from-[#FFDAB3] via-[#FFE4C9] to-[#FFDAB3] rounded-3xl py-16 px-8">
          <div className="max-w-[1400px] mx-auto">
            {/* Title */}
            <div className="text-center mb-12">
              <SkeletonText width="100px" className="h-3 mx-auto mb-3" />
              <SkeletonTitle size="xl" className="mx-auto w-80" />
            </div>
            
            {/* Testimonials */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white rounded-3xl p-6 space-y-4">
                  <div className="flex gap-1">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Skeleton key={j} className="w-4 h-4 rounded" />
                    ))}
                  </div>
                  <SkeletonParagraph lines={4} />
                  <div className="flex items-center gap-3 pt-2">
                    <Skeleton className="w-10 h-10 rounded-full" />
                    <div className="space-y-1">
                      <SkeletonText width="80px" className="h-4" />
                      <SkeletonText width="60px" className="h-3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section Skeleton */}
      <section className="py-4 px-3 pb-6">
        <div className="window-content bg-[#1E1E1E] rounded-3xl py-16 px-8">
          <div className="max-w-[800px] mx-auto text-center">
            <SkeletonTitle size="xl" className="mx-auto w-96 mb-6 bg-white/10" />
            <Skeleton className="h-6 w-80 mx-auto rounded mb-10 bg-white/10" />
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SkeletonButton size="xl" className="w-48 bg-white/20" />
              <SkeletonButton size="xl" className="w-44 bg-white/10" />
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
);

export { HomePageSkeleton };
